import { useCallback, useEffect, useRef, useState } from 'react';
import { AppBar, Box, Stack, Toolbar, Typography } from '@mui/material';
import Link from '@mui/material/Link';
import { alpha, styled, keyframes } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import WifiRoundedIcon from '@mui/icons-material/WifiRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import { useTranslation } from 'react-i18next';
import AuthService from '../../services/authService';
import AuthenticatedContent from '../../common/AuthenticatedContent';
import { useActiveTickets, WS_STATUS } from '../activeTickets/useActiveTickets';
import LanguageSwitcher from '../../common/LanguageSwitcher/LanguageSwitcher';

const HIDE_DELAY = 1800;
const BTN_HIDE_DELAY = 5000;

function useClock() {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    return now;
}

function useAutoFullscreen() {
    const [isFullscreen, setIsFullscreen] = useState(() => !!document.fullscreenElement);
    useEffect(() => {
        const onChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);
    const enter = useCallback(() => {
        if (!document.fullscreenElement)
            document.documentElement.requestFullscreen().catch(() => {});
    }, []);
    useEffect(() => {
        if (isFullscreen) return;
        const handler = () => enter();
        window.addEventListener('click',      handler, { once: true });
        window.addEventListener('keydown',    handler, { once: true });
        window.addEventListener('touchstart', handler, { once: true, passive: true });
        return () => {
            window.removeEventListener('click',      handler);
            window.removeEventListener('keydown',    handler);
            window.removeEventListener('touchstart', handler);
        };
    }, [isFullscreen, enter]);
    return { isFullscreen };
}

function extractYoutubeId(url) {
    if (!url) return null;
    const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/\s]{11})/);
    return m ? m[1] : null;
}

function useBtnVisibility() {
    const [visible, setVisible] = useState(true);
    const timer = useRef(null);
    const schedule = useCallback(() => {
        clearTimeout(timer.current);
        setVisible(true);
        timer.current = setTimeout(() => setVisible(false), BTN_HIDE_DELAY);
    }, []);
    useEffect(() => {
        schedule();
        window.addEventListener('mousemove', schedule);
        return () => {
            window.removeEventListener('mousemove', schedule);
            clearTimeout(timer.current);
        };
    }, [schedule]);
    return visible;
}

// ─── Главный компонент ────────────────────────────────────────────────────────
export const Head = () => {
    const { t, i18n } = useTranslation();
    const now = useClock();
    const { tickets, wsStatus } = useActiveTickets();
    useAutoFullscreen();
    const btnVisible = useBtnVisibility();

    const days   = t('tv.days',   { returnObjects: true });
    const months = t('tv.months', { returnObjects: true });
    const news   = t('tv.news',   { returnObjects: true });

    const timeStr = now.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const dateStr = `${Array.isArray(days) ? days[now.getDay()] : ''}, ${now.getDate()} ${Array.isArray(months) ? months[now.getMonth()] : ''} ${now.getFullYear()}`;

    // Показываем/скрываем хедер при наведении
    const [headerVisible, setHeaderVisible] = useState(false);
    const hideTimer = useRef(null);
    const showHeader   = () => { clearTimeout(hideTimer.current); setHeaderVisible(true); };
    const scheduleHide = () => { hideTimer.current = setTimeout(() => setHeaderVisible(false), HIDE_DELAY); };
    useEffect(() => () => clearTimeout(hideTimer.current), []);

    // Вызванные и ожидающие талоны
    const calledTickets  = (tickets || []).filter(t => String(t.ticketStatus).toUpperCase() === 'CALLED');
    const waitingTickets = (tickets || []).filter(t => String(t.ticketStatus).toUpperCase() === 'WAITING');

    // Анимация оверлея при вызове
    const [overlayTicket, setOverlayTicket] = useState(null);
    const prevCalledRef = useRef([]);

    // Эффект 1: определяем новые вызовы
    useEffect(() => {
        const prevNums = new Set(prevCalledRef.current.map(t => t.ticketNumber));
        const newlyCalled = calledTickets.filter(t => !prevNums.has(t.ticketNumber));
        if (newlyCalled.length > 0) {
            setOverlayTicket(newlyCalled[0]);
        }
        prevCalledRef.current = calledTickets;
    }, [calledTickets]); // eslint-disable-line react-hooks/exhaustive-deps

    // Эффект 2: таймер автозакрытия
    useEffect(() => {
        if (!overlayTicket) return;
        const timer = setTimeout(() => setOverlayTicket(null), 8000);
        return () => clearTimeout(timer);
    }, [overlayTicket]);

    const newsItems = Array.isArray(news) ? news : [];

    return (
        <DarkPage>
            {/* Декоративные фоновые элементы */}
            <BgBlob sx={{ width: 600, height: 600, top: -200, left: -200, background: 'rgba(37,99,235,0.08)' }} />
            <BgBlob sx={{ width: 500, height: 500, bottom: -150, right: -150, background: 'rgba(34,197,94,0.06)' }} />

            {/* ── Скрытый AppBar (наведение) ──────────────────────────── */}
            <Box onMouseEnter={showHeader} sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: 18, zIndex: 1200 }} />
            <AppBar
                position='fixed'
                elevation={0}
                onMouseEnter={showHeader}
                onMouseLeave={scheduleHide}
                sx={{
                    background:     'rgba(15,23,42,0.95)',
                    backdropFilter: 'blur(12px)',
                    borderBottom:   '1px solid rgba(255,255,255,0.1)',
                    zIndex:         1201,
                    transform:      headerVisible ? 'translateY(0)' : 'translateY(-100%)',
                    transition:     'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
            >
                <Toolbar sx={{ px: '1.5rem', minHeight: '3.5rem !important', justifyContent: 'space-between' }}>
                    <Stack direction='row' alignItems='center' spacing='0.75rem'>
                        <BrandLogo src='/logo5.png' alt='Dulaty University' />
                        <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '1rem' }}>
                            {t('tv.title')}
                        </Typography>
                    </Stack>
                    <Stack direction='row' spacing='0.5rem' alignItems='center'>
                        <LanguageSwitcher variant='dark' />
                        {!AuthService.isLoggedIn() && (
                            <NavLink component={RouterLink} to='/login' underline='none' color='inherit'>
                                <LoginRoundedIcon sx={{ fontSize: '1rem' }} />{t('common.login')}
                            </NavLink>
                        )}
                        {AuthService.isLoggedIn() && (
                            <>
                                <AuthenticatedContent roles={['ADMIN', 'MODERATOR']}>
                                    <NavLink component={RouterLink} to='/admin' underline='none' color='inherit'>
                                        <DashboardRoundedIcon sx={{ fontSize: '1rem' }} />{t('common.admin')}
                                    </NavLink>
                                </AuthenticatedContent>
                                <AuthenticatedContent roles={['MANAGER']}>
                                    <NavLink component={RouterLink} to='/manager' underline='none' color='inherit'>
                                        <LoginRoundedIcon sx={{ fontSize: '1rem' }} />{t('common.manager')}
                                    </NavLink>
                                </AuthenticatedContent>
                                <NavLink component={RouterLink} onClick={AuthService.logout} underline='none' color='inherit'>
                                    {t('common.logout')}
                                </NavLink>
                            </>
                        )}
                    </Stack>
                </Toolbar>
            </AppBar>

            {/* ── Основной контент ────────────────────────────────────── */}
            <MainLayout>
                {/* ── Шапка ─────────────────────────────────────────── */}
                <HeaderRow>
                    <Stack direction='row' alignItems='center' spacing={3}>
                        <LogoCircle>
                            <LogoImg src='/logo5.png' alt='Dulaty University' />
                        </LogoCircle>
                        <Box>
                            <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.8rem', md: '2.5rem', lg: '3rem' }, color: '#fff', lineHeight: 1.1 }}>
                                {t('tv.university')}
                            </Typography>
                            <Typography sx={{ fontSize: { xs: '1rem', md: '1.3rem' }, color: 'rgba(147,197,253,0.9)', mt: 0.5 }}>
                                {t('tv.title')}
                            </Typography>
                        </Box>
                    </Stack>

                    <Box sx={{ textAlign: 'right' }}>
                        <Stack direction='row' spacing={2} justifyContent='flex-end' alignItems='center' sx={{ mb: 1 }}>
                            <WifiRoundedIcon sx={{ color: wsStatus === WS_STATUS.CONNECTED ? '#4ade80' : '#9ca3af', fontSize: '2rem' }} />
                            <CalendarTodayRoundedIcon sx={{ color: '#93c5fd', fontSize: '2rem' }} />
                        </Stack>
                        <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.5rem', md: '2rem', lg: '2.5rem' }, color: '#fff', fontFamily: '"Roboto Mono", monospace', letterSpacing: '0.04em' }}>
                            {timeStr}
                        </Typography>
                        <Typography sx={{ fontSize: { xs: '0.8rem', md: '1rem' }, color: '#93c5fd', mt: 0.5 }}>
                            {dateStr}
                        </Typography>
                    </Box>
                </HeaderRow>

                {/* ── Тело ──────────────────────────────────────────── */}
                <ContentGrid>
                    {/* ── Левая 2/3: очередь ──────────────────────── */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, overflow: 'hidden' }}>

                        {/* Сейчас обслуживается */}
                        <GlassCard>
                            <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 3 }}>
                                <PulseDot />
                                <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.3rem', md: '1.8rem', lg: '2.2rem' }, color: '#fff' }}>
                                    {t('tv.nowServing')}
                                </Typography>
                            </Stack>

                            {calledTickets.length === 0 ? (
                                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '1.2rem', py: 2 }}>
                                    {t('tv.waitingFirst')}
                                </Typography>
                            ) : (
                                <Stack spacing={2}>
                                    {calledTickets.map(ticket => (
                                        <CalledRow key={ticket.ticketNumber}>
                                            <Stack direction='row' alignItems='center' spacing={4}>
                                                <Box>
                                                    <Typography sx={{ fontSize: '1rem', color: '#93c5fd', mb: 0.5, fontWeight: 600 }}>
                                                        {t('tv.desk')}
                                                    </Typography>
                                                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '3rem', md: '4rem', lg: '5rem' }, color: '#fff', lineHeight: 1 }}>
                                                        {String(ticket.windowNumber || '—').padStart(2, '0')}
                                                    </Typography>
                                                </Box>
                                                <Divider />
                                                <Box>
                                                    <Typography sx={{ fontSize: '1rem', color: '#93c5fd', mb: 0.5, fontWeight: 600 }}>
                                                        {t('tv.number')}
                                                    </Typography>
                                                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '3rem', md: '4rem', lg: '5rem' }, color: '#4ade80', lineHeight: 1 }}>
                                                        {ticket.ticketNumber}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </CalledRow>
                                    ))}
                                </Stack>
                            )}
                        </GlassCard>

                        {/* Следующие номера */}
                        <GlassCard sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.3rem', md: '1.8rem', lg: '2.2rem' }, color: '#fff', mb: 3 }}>
                                {t('tv.nextNumbers')}
                            </Typography>
                            {waitingTickets.length === 0 ? (
                                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '1.1rem' }}>
                                    {t('tv.queueEmpty')}
                                </Typography>
                            ) : (
                                <WaitingGrid>
                                    {waitingTickets.slice(0, 10).map(ticket => (
                                        <WaitingBadge key={ticket.ticketNumber}>
                                            <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.5rem', md: '2rem', lg: '2.5rem' }, color: '#fff' }}>
                                                {ticket.ticketNumber}
                                            </Typography>
                                        </WaitingBadge>
                                    ))}
                                </WaitingGrid>
                            )}
                        </GlassCard>
                    </Box>

                </ContentGrid>

                {/* ── Бегущая строка ────────────────────────────────── */}
                <TickerRow>
                    <TickerTrack>
                        {[...newsItems, ...newsItems].map((item, i) => (
                            <TickerItem key={i}>{item}</TickerItem>
                        ))}
                    </TickerTrack>
                </TickerRow>
            </MainLayout>

            {/* ── Оверлей вызова ────────────────────────────────────── */}
            {overlayTicket && (
                <CallOverlay onClick={() => setOverlayTicket(null)}>
                    <CallBox>
                        <Typography sx={{ fontWeight: 900, fontSize: { xs: '5rem', md: '8rem', lg: '10rem' }, color: '#fff', animation: `${pulseScale} 0.8s ease-in-out infinite`, lineHeight: 1 }}>
                            {overlayTicket.ticketNumber}
                        </Typography>
                        <CallCard>
                            <Typography sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3.5rem', lg: '4.5rem' }, color: '#22c55e', mb: 1 }}>
                                {t('tv.yourTurn')}
                            </Typography>
                            {overlayTicket.windowNumber && (
                                <Typography sx={{ fontSize: { xs: '1.5rem', md: '2.5rem' }, color: '#fff' }}>
                                    {t('tv.proceedToDesk', { number: String(overlayTicket.windowNumber).padStart(2, '0') })}
                                </Typography>
                            )}
                        </CallCard>
                    </CallBox>
                </CallOverlay>
            )}

            {/* ── Кнопки переключения дизайна ───────────────────────── */}
            <SwitchBtnGroup sx={{ opacity: btnVisible ? 1 : 0, pointerEvents: btnVisible ? 'auto' : 'none' }}>
                <DesignSwitchBtn component={RouterLink} to='/home2'>
                    {t('tv.design2')}
                </DesignSwitchBtn>
                <DesignSwitchBtn component={RouterLink} to='/'>
                    {t('tv.design3')}
                </DesignSwitchBtn>
            </SwitchBtnGroup>
        </DarkPage>
    );
};


// ─── Styled components ────────────────────────────────────────────────────────

const pulseScale = keyframes`
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
`;

const tickerMove = keyframes`
    0%   { transform: translateX(0); }
    100% { transform: translateX(-50%); }
`;

const pulseDotAnim = keyframes`
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.85); }
`;

const SwitchBtnGroup = styled(Box)(() => ({
    position:   'fixed',
    bottom:     '1.25rem',
    right:      '1.25rem',
    zIndex:     1100,
    display:    'flex',
    gap:        '0.5rem',
    transition: 'opacity 0.6s ease',
}));

const DesignSwitchBtn = styled(Link)(() => ({
    padding:        '0.4rem 0.9rem',
    borderRadius:   '0.6rem',
    fontWeight:     700,
    fontSize:       '0.75rem',
    color:          'rgba(255,255,255,0.7)',
    background:     'rgba(255,255,255,0.08)',
    backdropFilter: 'blur(8px)',
    border:         '1px solid rgba(255,255,255,0.15)',
    textDecoration: 'none',
    cursor:         'pointer',
    transition:     'background 0.2s ease, color 0.2s ease',
    '&:hover': {
        background: 'rgba(255,255,255,0.15)',
        color:      '#fff',
    },
}));

const DarkPage = styled(Box)(() => ({
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    color: '#fff',
}));

const BgBlob = styled(Box)(() => ({
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(80px)',
    pointerEvents: 'none',
    zIndex: 0,
}));

const MainLayout = styled(Box)(({ theme }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    gap: theme.spacing(3),
    position: 'relative',
    zIndex: 1,
    height: '100vh',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(2),
        gap: theme.spacing(2),
    },
}));

const HeaderRow = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
}));

const ContentGrid = styled(Box)(({ theme }) => ({
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(3),
    minHeight: 0,
    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr',
    },
}));

const GlassCard = styled(Box)(() => ({
    background: 'rgba(255,255,255,0.07)',
    backdropFilter: 'blur(12px)',
    borderRadius: 24,
    padding: 32,
    border: '1px solid rgba(255,255,255,0.12)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
}));


const LogoCircle = styled(Box)(() => ({
    width: 80,
    height: 80,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.15)',
    border: '2px solid rgba(255,255,255,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
}));

const LogoImg = styled('img')(() => ({
    width: 52,
    height: 52,
    objectFit: 'contain',
    filter: 'brightness(0) invert(1)',
}));

const CalledRow = styled(Box)(() => ({
    background: 'linear-gradient(90deg, rgba(30,58,138,0.8) 0%, rgba(30,64,175,0.6) 100%)',
    borderRadius: 16,
    padding: '20px 28px',
    border: '2px solid rgba(255,255,255,0.2)',
}));

const Divider = styled(Box)(() => ({
    width: 1,
    height: 80,
    background: 'rgba(255,255,255,0.25)',
    flexShrink: 0,
}));

const WaitingGrid = styled(Box)(() => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: 12,
}));

const WaitingBadge = styled(Box)(() => ({
    background: 'rgba(255,255,255,0.12)',
    borderRadius: 12,
    padding: '16px 8px',
    textAlign: 'center',
    border: '1px solid rgba(255,255,255,0.2)',
}));

const PulseDot = styled(Box)(() => ({
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: '#4ade80',
    flexShrink: 0,
    animation: `${pulseDotAnim} 1.5s ease-in-out infinite`,
}));

const TickerRow = styled(Box)(() => ({
    flexShrink: 0,
    background: 'rgba(255,255,255,0.07)',
    backdropFilter: 'blur(8px)',
    borderRadius: 12,
    padding: '12px 0',
    border: '1px solid rgba(255,255,255,0.1)',
    overflow: 'hidden',
}));

const TickerTrack = styled(Box)(() => ({
    display: 'flex',
    gap: 80,
    width: 'max-content',
    animation: `${tickerMove} 40s linear infinite`,
}));

const TickerItem = styled(Typography)(() => ({
    fontSize: '1.1rem',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.85)',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    paddingLeft: 40,
}));

const CallOverlay = styled(Box)(() => ({
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.85)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    cursor: 'pointer',
}));

const CallBox = styled(Box)(() => ({
    textAlign: 'center',
}));

const CallCard = styled(Box)(() => ({
    background: 'rgba(255,255,255,0.1)',
    backdropFilter: 'blur(12px)',
    borderRadius: 32,
    padding: '40px 60px',
    border: '3px solid #22c55e',
    marginTop: 32,
}));


const BrandLogo = styled('img')(() => ({
    width: '7rem',
    height: '1.9rem',
    objectFit: 'contain',
    filter: 'brightness(0) invert(1)',
}));

const NavLink = styled(Link)(() => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    fontWeight: 700,
    fontSize: '0.8rem',
    padding: '0.35rem 0.6rem',
    borderRadius: '0.5rem',
    color: '#fff',
    transition: 'background-color .2s ease',
    '&:hover': { backgroundColor: 'rgba(255,255,255,.1)' },
}));

export default Head;
